import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { TSettings } from '@/types'
import { getDefaultSettings } from '@/store/utils'
import { useJams, useJazelAppConfig } from '@jazel/jazel-vuetify'
import { useAccountSettingsStore } from '@/store/accountSettings'

type ImageSettings = {
  file: File | undefined
  width: number
  height: number | string
  align: 'right' | 'center' | 'left'
}

export const useProductSettingsStore = defineStore('productSettings', () => {
  const juid = ref('')

  const appConfig = useJazelAppConfig()
  const accountSettings = useAccountSettingsStore()
  const jamsService = useJams()
  const productSettings = ref<TSettings>(getDefaultSettings())

  const vehicleTitleSettings = ref<{
    selectedVehicleTitles: string[]
    displayCarVin: boolean
    displayCarStockNumber: boolean
    align: string
  }>({
    selectedVehicleTitles:
      getDefaultSettings().vehicleTitle.selectedVehicleTitles,
    displayCarVin: true,
    displayCarStockNumber: true,
    align: 'center',
  })

  const snackbarMessage = 'Product updated successfully'
  const showSnackbar = ref(false)
  const snackbarColor = ref('success')

  const aboveTextSettings = ref({
    align: 'center',
    text: '',
  })
  const belowTextSettings = ref({
    align: 'center',
    text: '',
  })
  const aboveImageSettings = ref<ImageSettings>({
    file: undefined,
    width: 200,
    height: 'auto',
    align: 'center',
  })
  const belowImageSettings = ref<ImageSettings>({
    file: undefined,
    width: 200,
    height: 'auto',
    align: 'center',
  })
  const typographySettings = ref({
    markupTextColor: '#000000',
    markupLineHeight: '1.5',
    markupLineHeightUnit: 'em',
    markupTextWeight: 'normal',
    markupFontSize: '16',
    markupFontSizeUnit: 'px',
    markupFontFamily: 'Roboto, sans-serif',
  })

  // car title - removed legacy variables, using vehicleTitleSettings instead
  // brand image
  const showBrandImage = ref(true)
  const brandImage = ref('')
  const imageUrl = ref<string>('')

  // session-only: use a custom image above the QR code instead of company logo
  const useCustomImageAbove = ref(false)

  //account id
  const accountId = ref('')
  const loading = ref(false)
  const error = ref()
  const settingsLoaded = ref(false)

  const setProductSettings = (settings: any | null) => {
    brandImage.value = accountSettings.getAccountLogo()
    // Load persisted show flag if present (default true)
    showBrandImage.value = settings?.showBrandImage ?? true
    // Load persisted useCustomImage toggle and URL
    useCustomImageAbove.value = settings?.useCustomImageAbove ?? false
    imageUrl.value = settings?.customImageUrl ?? ''
    aboveTextSettings.value = settings?.aboveTextSettings ?? {
      align: 'center',
      text: '',
    }
    belowTextSettings.value = settings?.belowTextSettings ?? {
      align: 'center',
      text: '',
    }
    aboveImageSettings.value = settings?.aboveImageSettings ?? {
      file: undefined,
      width: 200,
      height: 'auto',
      align: 'center',
    }
    belowImageSettings.value = settings?.belowImageSettings ?? {
      file: undefined,
      width: 200,
      height: 'auto',
      align: 'center',
    }
    typographySettings.value = settings?.typographySettings ?? {
      markupTextColor: '#000000',
      markupLineHeight: '1.5',
      markupLineHeightUnit: 'em',
      markupTextWeight: 'normal',
      markupFontSize: '16',
      markupFontSizeUnit: 'px',
      markupFontFamily: 'Roboto, sans-serif',
    }
    // Normalize vehicle title selections to lowercase for consistency
    const normalizeVehicleTitles = (titles: string[] | undefined) => {
      if (!titles || !Array.isArray(titles)) {
        return []
      }
      return titles.map(title => title.toLowerCase())
    }

    vehicleTitleSettings.value = {
      selectedVehicleTitles: normalizeVehicleTitles(
        settings?.vehicleTitle?.selectedVehicleTitles,
      ),
      displayCarVin: settings?.vehicleTitle?.displayCarVin ?? true,
      displayCarStockNumber:
        settings?.vehicleTitle?.displayCarStockNumber ??
        settings?.vehicleTitle?.displayStockNumber ??
        true,
      align: settings?.vehicleTitle?.align ?? 'center',
    }
  }

  const updateSettings = async () => {
    loading.value = true
    const payload: any = {
      ...productSettings.value,
      aboveTextSettings: aboveTextSettings.value,
      belowTextSettings: belowTextSettings.value,
      aboveImageSettings: aboveImageSettings.value,
      belowImageSettings: belowImageSettings.value,
      typographySettings: typographySettings.value,
      // Persist the brand image visibility toggle
      showBrandImage: showBrandImage.value,
      // Persist custom image toggle and URL
      useCustomImageAbove: useCustomImageAbove.value,
      customImageUrl: imageUrl.value,
      vehicleTitle: vehicleTitleSettings.value, // Use 'vehicleTitle' to match backend expectation
    }

    const params = {
      accountId: accountSettings.accountId,
      endpoint: appConfig.jamsBackendUrl,
      isClientUser: accountSettings.isClientUser,
      settings: payload,
      productId: appConfig.productId,
      token: accountSettings.sessionToken,
    }
    const savingResult = await jamsService.setProductSettings(params)

    if (!savingResult) {
      error.value = new Error(`Error saving product settings`)
      loading.value = false
    } else {
      loading.value = false
      showSnackbar.value = true
    }
  }

  return {
    //state
    aboveTextSettings,
    belowTextSettings,
    aboveImageSettings,
    belowImageSettings,
    typographySettings,
    vehicleTitleSettings,
    showBrandImage,
    accountId,
    snackbarMessage,
    showSnackbar,
    snackbarColor,
    settingsLoaded,
    error,
    loading,
    brandImage,
    imageUrl,
    useCustomImageAbove,

    // actions
    updateSettings,
    setProductSettings,
    juid,
  }
})
